import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ActivityIndicator} from 'react-native';
import Modal from '../../../components/Modal';
import {useDarkMode} from '../DarkModeContext';

function viewportSize() {
    return {
        width: Math.min(document.documentElement.clientWidth || window.innerWidth, window.visualViewport?.width || window.innerWidth),
        height: window.visualViewport?.height || window.innerHeight,
        top: window.visualViewport?.offsetTop || 0,
        left: window.visualViewport?.offsetLeft || 0,
    };
}

export default function GrammarReviewModal({original, corrected, pending = false, error = null, canApply, onApply, onClose, onDismiss}) {
    const {theme} = useDarkMode();
    const [viewport, setViewport] = useState(viewportSize);
    const overlayPressStarted = useRef(false);
    const modalPressStarted = useRef(false);
    const requestClose = useCallback(event => {
        if (event?.type === 'click' && event.detail > 0 && !modalPressStarted.current) return false;
        modalPressStarted.current = false;
        onClose();
        return true;
    }, [onClose]);
    const panel = useRef(null);
    const closeButton = useRef(null);
    useEffect(() => {
        // The tap opening the modal began before this listener existed. Its
        // trailing click must not be treated as a new tap on the backdrop.
        const pointerDown = event => {
            modalPressStarted.current = true;
            overlayPressStarted.current = !!panel.current && !panel.current.contains(event.target);
        };
        const pointerCancel = () => { overlayPressStarted.current = false; modalPressStarted.current = false; };
        document.addEventListener('pointerdown', pointerDown, true);
        document.addEventListener('pointercancel', pointerCancel, true);
        return () => {
            document.removeEventListener('pointerdown', pointerDown, true);
            document.removeEventListener('pointercancel', pointerCancel, true);
        };
    }, []);
    const closeFromOverlay = () => {
        const pressedOutside = overlayPressStarted.current;
        overlayPressStarted.current = false;
        if (pressedOutside) requestClose();
    };
    useEffect(() => {
        const previousFocus = document.activeElement;
        closeButton.current.focus({preventScroll: true});
        const resize = () => setViewport(viewportSize());
        const keyDown = event => {
            if (event.key === 'Escape') { event.preventDefault(); requestClose(); }
            if (event.key !== 'Tab') return;
            const buttons = Array.from(panel.current.querySelectorAll('button:not(:disabled)'));
            const first = buttons[0];
            const last = buttons[buttons.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        window.addEventListener('resize', resize);
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', resize);
            window.visualViewport.addEventListener('scroll', resize);
        }
        document.addEventListener('keydown', keyDown);
        return () => {
            window.removeEventListener('resize', resize);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', resize);
                window.visualViewport.removeEventListener('scroll', resize);
            }
            document.removeEventListener('keydown', keyDown);
            if (previousFocus?.isConnected) previousFocus.focus({preventScroll: true});
        };
    }, [requestClose]);
    const mobile = viewport.width <= 760;
    const button = {font: 'inherit', minHeight: 44, padding: '10px 16px', borderRadius: 6, border: `1px solid ${theme.border}`, background: theme.background, color: theme.text, cursor: 'pointer'};
    return <Modal active onClose={closeFromOverlay} closeOnOverlayClick
        style={{zIndex: 1100, top: viewport.top, left: viewport.left, right: 'auto', bottom: 'auto', width: viewport.width, height: viewport.height}}
        innerStyle={{width: Math.max(0, Math.min(760, viewport.width - 24)), maxWidth: '100%', minWidth: 0}}>
        <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="grammar-review-title"
            style={{boxSizing: 'border-box', display: 'flex', flexDirection: 'column', width: '100%', maxHeight: Math.max(0, viewport.height - 24), minWidth: 0, overflow: 'hidden', borderRadius: 12, background: theme.background, color: theme.text, fontFamily: 'SourceSansPro-Regular, sans-serif'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', flexShrink: 0, borderBottom: `1px solid ${theme.border}`}}>
                <h2 id="grammar-review-title" style={{fontSize: 20, margin: 0}}>Review grammar</h2>
                <button ref={closeButton} aria-label="Close grammar review" onClick={requestClose} style={button}>×</button>
            </div>
            <div style={{overflowY: 'auto', minHeight: 0, minWidth: 0, padding: 16}}>
                {pending ? <div role="status" style={{padding: '24px 0', textAlign: 'center'}}><ActivityIndicator color="#0065DB"/><p>Checking grammar…</p></div> : error ? <p role="alert">{error}</p> : <>
                {!canApply && <p>Your composer has changed. This correction cannot replace the current draft.</p>}
                {original === corrected && <p>No grammar changes needed.</p>}
                <div style={{display: 'grid', gridTemplateColumns: mobile ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: 12}}>
                    {[['Original', original], ['Corrected', corrected]].map(([label, text]) => <div key={label} style={{minWidth: 0, padding: 12, border: `1px solid ${theme.border}`, borderRadius: 8}}>
                        <h3 style={{fontSize: 15, margin: '0 0 8px'}}>{label}</h3>
                        <p style={{margin: 0, fontSize: 16, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{text}</p>
                    </div>)}
                </div>
                </>}
            </div>
            {!pending && <div style={{display: 'flex', flexDirection: mobile ? 'column-reverse' : 'row', justifyContent: 'flex-end', gap: 8, padding: 16, paddingBottom: 'max(16px, env(safe-area-inset-bottom))', flexShrink: 0, borderTop: `1px solid ${theme.border}`}}>
                <button onClick={error || !canApply ? (onDismiss || requestClose) : () => onApply(original)} style={button}>{error || !canApply ? 'Dismiss review' : 'Keep existing'}</button>
                {!error && <button onClick={() => onApply(corrected)} disabled={!canApply} style={{...button, background: '#0065DB', color: '#fff', opacity: canApply ? 1 : 0.5}}>Use correction</button>}
            </div>}
        </div>
    </Modal>;
}
